/**
 * Rate Predictor — slider calculations and optional email gate.
 *
 * Handles the three-slider rate estimation widget. If the email gate is
 * present (.js-rate-predictor-gate), the rate result stays hidden until
 * the user submits a valid email via the WordPress AJAX endpoint.
 *
 * @file rate-predictor.js
 */
(() => {
  'use strict';

  const ageSlider      = document.getElementById('age');
  const premiumSlider  = document.getElementById('premium');
  const deferralSlider = document.getElementById('deferral');

  const ageOutput      = document.getElementById('ageOutput');
  const premiumOutput  = document.getElementById('premiumOutput');
  const deferralOutput = document.getElementById('deferralOutput');
  const rateDisplay    = document.getElementById('rate');

  if (!ageSlider || !premiumSlider || !deferralSlider ||
      !ageOutput || !premiumOutput || !deferralOutput || !rateDisplay) {
    return;
  }

  /**
   * Estimates the annuity rate from the three slider inputs.
   * Base rate of 3%, adjusted by deferral, premium, and age, clamped to [3, 7.5]%.
   *
   * @param {number} age
   * @param {number} premium
   * @param {number} deferral
   * @returns {number}
   */
  const getRate = (age, premium, deferral) => {
    const rate = 3 + (deferral * 0.25) + (premium / 1000000) * 0.5 + ((age - 50) * 0.05);
    return Math.min(Math.max(rate, 3), 7.5);
  };

  /**
   * @param {number} age
   * @param {number} premium
   * @param {number} deferral
   * @param {number} rate
   * @returns {void}
   */
  const setOutputs = (age, premium, deferral, rate) => {
    ageOutput.textContent      = age;
    premiumOutput.textContent  = premium.toLocaleString();
    deferralOutput.textContent = deferral;
    rateDisplay.textContent    = `${rate.toFixed(2)}%`;
  };

  /**
   * @returns {void}
   */
  const calculateRate = () => {
    const age      = Number(ageSlider.value);
    const premium  = Number(premiumSlider.value);
    const deferral = Number(deferralSlider.value);
    setOutputs(age, premium, deferral, getRate(age, premium, deferral));
  };

  ageSlider.addEventListener('input', calculateRate);
  premiumSlider.addEventListener('input', calculateRate);
  deferralSlider.addEventListener('input', calculateRate);
  document.addEventListener('DOMContentLoaded', calculateRate);

  /**
   * Bound to `change`, not `input`: `input` fires continuously while a slider is dragged, so
   * tracking it would emit hundreds of events for a single adjustment.
   *
   * @param {string} sliderName - Age, Premium or Deferral.
   * @param {string} value - The value settled on.
   */
  const trackAdjustment = (sliderName, value) => {
    if (typeof window.customEvent !== 'function') {
      return;
    }

    window.customEvent('Rates', {
      action: 'Adjust',
      label: sliderName,
      value: value,
      location: ageSlider.closest('[data-location]')?.dataset.location || 'rate-predictor',
      microIgnore: false,
      nonInteraction: false
    });
  };

  ageSlider.addEventListener('change', () => trackAdjustment('Age', ageSlider.value));
  premiumSlider.addEventListener('change', () => trackAdjustment('Premium', premiumSlider.value));
  deferralSlider.addEventListener('change', () => trackAdjustment('Deferral', deferralSlider.value));

  const ratePredictor = ageSlider.closest('.c-rate-predictor');

  // Email gate — only active when [rate_predictor gated] or a CPT gate is used.
  const builtInGateContainer = ratePredictor ? ratePredictor.querySelector('.js-rate-predictor-gate') : null;
  const cptGateContainer     = ratePredictor ? ratePredictor.querySelector('[data-email-capture-gate="rate-predictor"]') : null;

  if (!builtInGateContainer && !cptGateContainer) return;

  const rateResultContainer = ratePredictor ? ratePredictor.querySelector('.js-rate-predictor-result') : null;
  const ctaContainer        = ratePredictor ? ratePredictor.querySelector('.js-rate-predictor-ctas') : null;
  const STORAGE_KEY         = 'annuity_rp_unlocked';

  /**
   * Reveals the rate result and CTAs. The CPT gate stays visible when
   * `keepGateVisible` is true so the thanks message rendered inside it
   * (by email-capture.js's showSuccess) is not hidden along with the form.
   * On reload-from-localStorage there's no thanks message to preserve, so
   * the gate is hidden as it is for the built-in legacy gate.
   *
   * @param {{ keepGateVisible?: boolean }} [options]
   * @returns {void}
   */
  const unlockResult = ({ keepGateVisible = false } = {}) => {
    if (builtInGateContainer) {
      builtInGateContainer.hidden = true;
    }

    if (cptGateContainer && !keepGateVisible) {
      cptGateContainer.hidden = true;
    }

    if (rateResultContainer) {
      rateResultContainer.hidden = false;
    }

    if (ctaContainer) {
      ctaContainer.hidden = false;
    }
  };

  let isUnlocked = false;

  try {
    isUnlocked = localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    isUnlocked = false;
  }

  if (isUnlocked) {
    unlockResult();
    return;
  }

  // CPT gate — email-capture.js fires this event on successful submission.
  // Keep the gate visible so its thanks message stays on screen.
  if (cptGateContainer) {
    document.addEventListener('ratePredictorUnlocked', () => unlockResult({ keepGateVisible: true }), { once: true });
    return;
  }

  // TODO(legacy-rate-predictor-gated): Everything below this line — the
  // built-in gate submit handler, its honeypot/nonce/AJAX path, and the
  // `builtInGateContainer` references above — exists only for
  // [rate_predictor gated]. Once that variant is gone, delete from here
  // to the end of the IIFE and simplify the early-return condition above
  // (only cptGateContainer needs to be checked).
  const emailForm       = builtInGateContainer.querySelector('.js-rate-predictor-form');
  const submitButton    = builtInGateContainer.querySelector('.js-rate-predictor-submit');
  const errorMessage    = builtInGateContainer.querySelector('.js-rate-predictor-error');
  const honeypotInput   = emailForm.querySelector('input[name="website_url"]');

  /**
   * The legacy gate posts through its own fetch and carries no js-validateForm, so the shared
   * form pipeline never sees it. 9 of the 10 live [rate_predictor] pages still use this variant.
   *
   * @param {string} action - Submit, Success or Error.
   * @param {string} [reason] - Failure detail; Mixpanel only.
   */
  const trackGate = (action, reason) => {
    if (typeof window.customEvent !== 'function') {
      return;
    }

    window.customEvent('Form', {
      action: action,
      step: 0,
      title: emailForm.dataset.trackTitle,
      location: emailForm.closest('[data-location]')?.dataset.location || 'rate-predictor',
      offer: emailForm.dataset.trackOffer || 'rate-predictor-gate',
      reason: reason,
      microIgnore: false,
      nonInteraction: false
    });
  };

  emailForm.addEventListener('submit', async (submitEvent) => {
    submitEvent.preventDefault();

    const emailInput    = emailForm.querySelector('input[name="email"]');
    const emailValue    = emailInput.value.trim();
    const honeypotValue = honeypotInput?.value ?? '';

    if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue)) {
      emailInput.setAttribute('aria-invalid', 'true');
      errorMessage.textContent = 'Please enter a valid email address.';
      errorMessage.hidden = false;
      emailInput.focus();
      trackGate('Error', 'Invalid Email');
      return;
    }

    emailInput.removeAttribute('aria-invalid');
    errorMessage.hidden = true;
    trackGate('Submit');

    const originalButtonLabel = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    submitButton.textContent = 'Submitting…';

    const resetButton = () => {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
      submitButton.textContent = originalButtonLabel;
    };

    const showError = (message) => {
      resetButton();
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    };

    try {
      // Fetch a fresh nonce at submit time so cached pages never serve a stale one.
      const nonceFormData = new FormData();
      nonceFormData.append('action', 'get_rate_predictor_nonce');
      const nonceResponse = await fetch(window.RatePredictorConfig?.ajaxUrl, { method: 'POST', body: nonceFormData });
      const nonceResult   = await nonceResponse.json();

      const subscribeFormData = new FormData();
      subscribeFormData.append('action', 'rate_predictor_subscribe');
      subscribeFormData.append('nonce', nonceResult.data.nonce);
      subscribeFormData.append('email', emailValue);
      subscribeFormData.append('source_url', window.location.href);
      subscribeFormData.append('honeypot_value', honeypotValue);

      const subscribeResponse = await fetch(window.RatePredictorConfig?.ajaxUrl, { method: 'POST', body: subscribeFormData });
      const subscribeResult   = await subscribeResponse.json();

      if (subscribeResult.success) {
        trackGate('Success');
        try {
          localStorage.setItem(STORAGE_KEY, '1');
        } catch {
          // Storage blocked; the successful subscribe should still unlock.
        }
        unlockResult();
      } else {
        const failureMessage = subscribeResult.data?.message || 'Something went wrong. Please try again.';
        trackGate('Error', failureMessage);
        showError(failureMessage);
      }
    } catch {
      trackGate('Error', 'Request Failed');
      showError('Something went wrong. Please try again.');
    }
  });
})();
